/**
 * World — 권위 상태 + 결정적 step(dt). 같은 시드 + 같은 dt 수열 = 같은 결과.
 * 프로토타입의 setInterval(벽시계)들을 dt 누적 타이머로 바꿔 결정성을 확보.
 */
import { ECONOMY, SPAWN, WORLD, type BuildId, type ParcelType } from '@villager/shared';
import { updateGroups, newGroup } from './adventurer.js';
import * as econ from './economy.js';
import { initBossesAndKing, spawnMob } from './enemy.js';
import { EventLog } from './events.js';
import { Rng } from './rng.js';
import type { Dest, Enemy, Group, Patron, PendingRaid, Player, Relic, Tile, Tomb } from './state.js';

/** 싱글/로컬 플레이어 id. 멀티에서는 접속마다 고유 id가 생긴다. */
export const LOCAL_PLAYER = 'me';

/** 밸런스 리포트용 누적 통계. */
export interface WorldStats {
  spawned: number;
  mobKills: number;
  namedKills: number;
  bossKills: number;
  merges: number;
  disbands: number;
  leaderChanges: number;
  purchases: number;
  /** 등급별 사망 수 (index=grade) */
  deathsByGrade: number[];
  /** 사망 시 (tick - bornTick) 누적 — 평균 수명 계산용 */
  lifetimeSum: number;
  lifetimeCount: number;
}

/** 시즌 결산 = 개인 연대기 요약 (지시서 M3). */
export interface SeasonResult {
  heroName: string;
  heroGrade: number;
  dead: number;
  sales: number;
  /** 순이익 = 매출 - (세금+유지비+수수료) */
  netProfit: number;
  taxPaid: number;
  feesPaid: number;
  parcels: number;
  tombs: number;
  seasonSeconds: number;
  /** 내 가게 단골 상위 (방문 많은 순) */
  topPatrons: { name: string; visits: number; spent: number; grade: number }[];
  /** 용사가 내 단골이었나 — 이 게임의 핵심 감정 */
  heroWasPatron: boolean;
  heroVisits: number;
  heroSpent: number;
}

export interface FeedMessage {
  id: number;
  msg: string;
  red: boolean;
}

export class World {
  readonly seed: number;
  readonly rng: Rng;
  readonly log = new EventLog();

  grid: Tile[][] = [];
  frontRow: number = WORLD.FRONT_ROW_INIT;

  deadCount = 0;
  pushKills = 0;
  seasonOver = false;
  seasonResult: SeasonResult | null = null;

  /** 방어 실패 누적 — RETREAT_FAILS 마다 전선 후퇴 */
  defenseFails = 0;

  /** 플레이어(마을사람) 명부. 멀티는 여러 명, 싱글은 LOCAL_PLAYER 1명. */
  players = new Map<string, Player>();

  // ── 로컬 플레이어 프록시 (싱글 코드/클라이언트 하위 호환) ──
  get local(): Player {
    return this.player(LOCAL_PLAYER);
  }
  get gold(): number {
    return this.local.gold;
  }
  set gold(v: number) {
    this.local.gold = v;
  }
  get sales(): number {
    return this.local.sales;
  }
  set sales(v: number) {
    this.local.sales = v;
  }
  get taxPaid(): number {
    return this.local.taxPaid;
  }
  get upkeepPaid(): number {
    return this.local.upkeepPaid;
  }
  get feesPaid(): number {
    return this.local.feesPaid;
  }
  /** 로컬 플레이어의 단골 장부 */
  get patrons(): Map<number, Patron> {
    return this.local.patrons;
  }

  tick = 0;
  time = 0;

  groups: Group[] = [];
  mobs: Enemy[] = [];
  bosses: Enemy[] = [];
  demonKing: Enemy | null = null;
  relics: Relic[] = [];
  tombs: Tomb[] = [];
  pendingRaids: PendingRaid[] = [];

  /**
   * 틱마다 1회 갱신되는 서비스 필지 캐시.
   * 그룹마다 그리드를 스캔하면 O(그룹수 × 타일수)라 1만 틱 시뮬에서 병목이 된다.
   */
  services: Record<'inn' | 'eat' | 'shop' | 'train', Dest | null> = {
    inn: null,
    eat: null,
    shop: null,
    train: null,
  };

  stats: WorldStats = {
    spawned: 0,
    mobKills: 0,
    namedKills: 0,
    bossKills: 0,
    merges: 0,
    disbands: 0,
    leaderChanges: 0,
    purchases: 0,
    deathsByGrade: [0, 0, 0, 0, 0, 0, 0],
    lifetimeSum: 0,
    lifetimeCount: 0,
  };

  // 타이머 (초 누적)
  prodT = 0;
  raidT = 0;
  taxT = 0;
  groupSpawnT = 0;
  mobSpawnT = 0;

  // client 통신용 버퍼
  private feedSeq = 0;
  feedMessages: FeedMessage[] = [];
  dirtyParcels = new Set<string>();
  frontDirty = false;

  private idCounter = 0;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    this.rng = new Rng(this.seed);
    this.addPlayer(LOCAL_PLAYER, '나');

    // 타일 그리드 (프로토타입: forest 확률, c-외 r-내 순회)
    for (let c = 0; c < WORLD.GW; c++) {
      const col: Tile[] = [];
      for (let r = 0; r < WORLD.GD; r++) {
        col.push({ parcel: null, forest: this.rng.chance(WORLD.FOREST_CHANCE), tombs: 0 });
      }
      this.grid.push(col);
    }

    initBossesAndKing(this);

    // 초기 모험가: 솔로 3 + 파티 1 (세계는 한산하게 시작한다)
    for (let i = 0; i < 3; i++) newGroup(this, 1);
    newGroup(this, 2 + this.rng.int(2));
  }

  nextId(): number {
    return ++this.idCounter;
  }

  feed(msg: string, red = false): void {
    this.feedMessages.push({ id: this.feedSeq++, msg, red });
    if (this.feedMessages.length > 12) this.feedMessages.shift();
  }

  markParcelDirty(c: number, r: number): void {
    this.dirtyParcels.add(`${c},${r}`);
  }

  /** 소유(필지 있는) 타일 목록. */
  ownedTiles(): [number, number][] {
    const out: [number, number][] = [];
    for (let c = 0; c < WORLD.GW; c++) {
      for (let r = 0; r < WORLD.GD; r++) {
        if (this.grid[c]![r]!.parcel) out.push([c, r]);
      }
    }
    return out;
  }

  /** 총 모험가 수. */
  get adventurerCount(): number {
    return this.groups.reduce((s, g) => s + g.members.length, 0);
  }

  /** 해방률(%) — 프로토타입 표기와 동일. */
  get liberationPct(): number {
    return Math.max(0, Math.round(((WORLD.FRONT_ROW_INIT - this.frontRow) / 3) * 100));
  }

  /** 플레이어 등록 (멀티 접속 / 게스트 즉시 시작). 이미 있으면 기존 반환. */
  addPlayer(id: string, name = '마을사람'): Player {
    const existing = this.players.get(id);
    if (existing) return existing;
    const p: Player = {
      id,
      name,
      gold: ECONOMY.START_GOLD,
      sales: 0,
      taxPaid: 0,
      upkeepPaid: 0,
      feesPaid: 0,
      patrons: new Map(),
      joinedTick: this.tick,
    };
    this.players.set(id, p);
    return p;
  }

  /** 플레이어 조회 (없으면 자동 생성 — 게스트 즉시 시작 원칙). */
  player(id: string): Player {
    return this.players.get(id) ?? this.addPlayer(id);
  }

  /** 특정 플레이어가 소유한 타일. */
  ownedBy(playerId: string): [number, number][] {
    return this.ownedTiles().filter(([c, r]) => this.grid[c]![r]!.parcel?.owner === playerId);
  }

  /** 총 유출(싱크) — 지정 플레이어(기본 로컬). */
  sinkOf(playerId = LOCAL_PLAYER): number {
    const p = this.player(playerId);
    return p.taxPaid + p.upkeepPaid + p.feesPaid;
  }

  /** 총 유출(싱크) 금액 (로컬). */
  get totalSink(): number {
    return this.sinkOf(LOCAL_PLAYER);
  }

  /** 순이익 = 매출 - 유출 (로컬). */
  get netProfit(): number {
    return Math.round(this.sales - this.totalSink);
  }

  /** 예고된 습격까지 남은 초 (없으면 null) — client 예고 UI용. */
  get raidWarning(): number | null {
    let min: number | null = null;
    for (const r of this.pendingRaids) {
      if (min === null || r.delay < min) min = r.delay;
    }
    return min;
  }

  /** 단골 장부 기록 — 해당 필지 소유자의 장부에 남는다. */
  recordPatron(ownerId: string, advId: number, name: string, grade: number, spent: number): void {
    const book = this.player(ownerId).patrons;
    const p = book.get(advId);
    if (p) {
      p.visits++;
      p.spent += spent;
      p.topGrade = Math.max(p.topGrade, grade);
    } else {
      book.set(advId, { id: advId, name, visits: 1, spent, topGrade: grade });
    }
  }

  /** 방문 많은 순 단골 상위 N (기본 로컬 플레이어). */
  topPatrons(n = 3, playerId = LOCAL_PLAYER): Patron[] {
    return [...this.player(playerId).patrons.values()]
      .sort((a, b) => b.visits - a.visits || b.spent - a.spent || a.id - b.id)
      .slice(0, n);
  }

  step(dt: number): void {
    if (this.seasonOver) return;
    this.tick++;
    this.time += dt;

    updateGroups(this, dt);
    econ.production(this, dt);
    econ.raidTick(this, dt);
    econ.taxTick(this, dt);

    // 스폰 (dt 누적 타이머)
    this.groupSpawnT += dt;
    if (this.groupSpawnT >= SPAWN.GROUP_INTERVAL) {
      this.groupSpawnT = 0;
      if (this.groups.length < SPAWN.GROUP_CAP) {
        newGroup(this, this.rng.chance(0.55) ? 1 : 2 + this.rng.int(2));
      }
    }
    this.mobSpawnT += dt;
    if (this.mobSpawnT >= 1.8) {
      this.mobSpawnT = 0;
      // 마왕령 압축: 영토가 좁아질수록 스폰 상한이 낮아진다(양↓, 격↑은 spawnMob에서)
      const ratio = this.frontRow / WORLD.FRONT_ROW_INIT;
      const cap = Math.max(
        SPAWN.MIN_MOB_CAP,
        Math.round(SPAWN.BASE_MOB_CAP * (SPAWN.CAP_FLOOR + (1 - SPAWN.CAP_FLOOR) * ratio)),
      );
      if (this.mobs.length < cap) spawnMob(this);
    }
  }

  // ── 플레이어 인텐트 (client/서버에서 호출). playerId 생략 시 로컬. ──
  buyParcel(c: number, r: number, type: ParcelType, playerId = LOCAL_PLAYER): boolean {
    return econ.buyParcel(this, c, r, type, playerId);
  }
  clearForest(c: number, r: number, playerId = LOCAL_PLAYER): boolean {
    return econ.clearForest(this, c, r, playerId);
  }
  repair(c: number, r: number, playerId = LOCAL_PLAYER): boolean {
    return econ.repairParcel(this, c, r, playerId);
  }
  build(c: number, r: number, i: number, j: number, b: BuildId, playerId = LOCAL_PLAYER): boolean {
    return econ.buildCell(this, c, r, i, j, b, playerId);
  }
  demolish(c: number, r: number, i: number, j: number, playerId = LOCAL_PLAYER): boolean {
    return econ.demolishCell(this, c, r, i, j, playerId);
  }

  /** 결정성 검증용 상태 서명 (두 런이 같으면 같은 문자열). */
  signature(): string {
    let acc = 0;
    const mix = (n: number): void => {
      acc = (Math.imul(acc ^ (n | 0), 0x9e3779b1) + 0x85ebca6b) | 0;
    };
    mix(this.tick);
    mix(Math.round(this.gold));
    mix(this.sales);
    mix(this.deadCount);
    mix(this.frontRow);
    mix(this.groups.length);
    mix(this.mobs.length);
    mix(this.log.count);
    for (const g of this.groups) {
      for (const a of g.members) {
        mix(Math.round(a.pos.x * 100));
        mix(Math.round(a.pos.z * 100));
        mix(Math.round(a.hp));
        mix(a.grade);
        mix(a.weapon);
      }
    }
    for (const m of this.mobs) {
      mix(Math.round(m.pos.x * 100));
      mix(Math.round(m.pos.z * 100));
      mix(Math.round(m.hp));
    }
    return `${this.tick}:${this.gold | 0}:${this.deadCount}:${this.frontRow}:${(acc >>> 0).toString(16)}`;
  }
}

export interface SimSummary {
  ticks: number;
  seasonOver: boolean;
  gold: number;
  sales: number;
  deadCount: number;
  frontRow: number;
  groupCount: number;
  advCount: number;
  mobCount: number;
  events: number;
  signature: string;
  seasonResult: SeasonResult | null;
}

/**
 * 헤드리스 시뮬 (지시서 M1 완료 기준). 고정 dt로 ticks 스텝 진행.
 * 렌더 없이 sim-core만으로 실행 가능해야 한다.
 */
export function simulate(seed: number, ticks: number, dt = 1 / 30): SimSummary {
  const w = new World(seed);
  for (let i = 0; i < ticks && !w.seasonOver; i++) w.step(dt);
  return {
    ticks: w.tick,
    seasonOver: w.seasonOver,
    gold: Math.round(w.gold),
    sales: w.sales,
    deadCount: w.deadCount,
    frontRow: w.frontRow,
    groupCount: w.groups.length,
    advCount: w.adventurerCount,
    mobCount: w.mobs.length,
    events: w.log.count,
    signature: w.signature(),
    seasonResult: w.seasonResult,
  };
}
